/**
 * brainwaves.js — pure helpers shared across the audio engine UI.
 *
 * Extracted from BrainwaveGenerator.jsx (Phase 1 refactor). Everything here
 * must be a pure function — no React state, no refs, no side effects.
 * Component-state-aware variants (e.g. heartbeat-aware getBrainwaveType) stay
 * inline in the component as thin wrappers around these primitives.
 */

/**
 * Standard brainwave frequency ranges + display colors.
 * Values were chosen for vibrant on-dark-theme legibility, not pastel.
 */
export const BRAINWAVE_RANGES = {
  delta: { min: 0.5, max: 4,   color: '#7C3AED', name: 'Delta' },
  theta: { min: 4,   max: 8,   color: '#00D4FF', name: 'Theta' },
  alpha: { min: 8,   max: 13,  color: '#00FF88', name: 'Alpha' },
  beta:  { min: 13,  max: 30,  color: '#FF8800', name: 'Beta'  },
  gamma: { min: 30,  max: 100, color: '#FF0044', name: 'Gamma' },
};

/**
 * Resolve a frequency (Hz) to its brainwave band.
 * Returns the matching range entry merged with its `type` key, or a "custom"
 * fallback when the value sits outside every band.
 */
export function getBrainwaveRange(frequency) {
  for (const [type, range] of Object.entries(BRAINWAVE_RANGES)) {
    if (frequency >= range.min && frequency <= range.max) {
      return { type, ...range };
    }
  }
  return { type: 'custom', color: '#9CA3AF', name: 'Custom' };
}

const _DESCRIPTIONS = {
  delta:  'Deep sleep, healing, unconscious processes',
  theta:  'Meditation, creativity, REM sleep',
  alpha:  'Relaxed awareness, light meditation',
  beta:   'Active thinking, problem solving',
  gamma:  'Heightened awareness, peak performance',
  custom: 'Custom frequency range',
};

/** Human-friendly description for a brainwave band. */
export function getBrainwaveDescription(type) {
  return _DESCRIPTIONS[type] || 'Unknown frequency range';
}

/** Format a duration in seconds as `M:SS`. NaN/Infinity → '0:00'. */
export function formatTime(seconds) {
  if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Build a default filename for an exported audio session.
 *
 * @param {Object} args
 * @param {string} args.beatType   e.g. 'binaural', 'monaural', 'heartbeat'
 * @param {number} args.freq       Current frequency in Hz
 * @param {number} args.duration   Session duration in seconds
 * @returns {string} `NeuroWave_<type>_<freq>Hz_<dur>s_<YYYY-MM-DD>_<HH-MM-SS>`
 */
export function generateExportFilename({ beatType, freq, duration }) {
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
  const freqStr = Number(freq).toFixed(1).replace('.', '_');
  return `NeuroWave_${beatType}_${freqStr}Hz_${duration}s_${dateStr}_${timeStr}`;
}
